import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import { nb } from "date-fns/locale"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

/**
 * Calendar (react-day-picker v8) – Norwegian locale, Monday first.
 * size: "md" (40px cells, desktop) | "lg" (44px cells, mobile / drawer).
 * Pass captionLayout="dropdown-buttons" for month/year dropdowns (fromYear/toYear are defaulted).
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = nb,
  weekStartsOn = 1,
  size = "md",
  fromYear,
  toYear,
  ...props
}) {
  const lg = size === "lg"
  const cell = lg ? "h-11 w-11" : "h-10 w-10"
  const head = lg ? "w-11" : "w-10"
  const thisYear = new Date().getFullYear()
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      weekStartsOn={weekStartsOn}
      fromYear={fromYear ?? thisYear - 10}
      toYear={toYear ?? thisYear + 10}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-3",
        caption: "flex justify-center pt-1 relative items-center h-10",
        caption_label: cn("text-sm font-semibold capitalize font-heading", props.captionLayout?.startsWith("dropdown") && "hidden"),
        caption_dropdowns: "flex items-center gap-1.5",
        dropdown: "h-9 appearance-none rounded-full border bg-card px-3 pr-7 text-sm font-semibold capitalize font-heading text-foreground shadow-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/60 hover:bg-accent cursor-pointer",
        dropdown_month: "relative",
        dropdown_year: "relative",
        dropdown_icon: "pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 opacity-60",
        vhidden: "sr-only",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-9 w-9 rounded-full bg-transparent p-0 opacity-80 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: cn("text-muted-foreground rounded-md font-medium text-[0.72rem] uppercase tracking-wide", head),
        row: "flex w-full mt-1.5",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-full",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-full [&:has(>.day-range-start)]:rounded-l-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full"
            : "[&:has([aria-selected])]:rounded-full"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          cell,
          "rounded-full p-0 font-normal tabular-nums aria-selected:opacity-100 active:scale-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground font-bold shadow-sm hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "ring-2 ring-inset ring-primary/40 font-semibold",
        day_outside:
          "day-outside text-muted-foreground/50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-40",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground aria-selected:font-normal",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
